import { Address, Cart, Discount, GiftCard, LineItem } from '@medusajs/medusa'

import axios from '@/core/axios'

export type WithCartId = {
    cart_id: string
}

export type CreateCartDto = {
    region_id?: string
    sales_channel_id?: string
    country_code?: string
    items?: Pick<LineItem, 'variant_id' | 'quantity'>[]
    context?: Record<string, unknown>
}

export type UpdateCartDto = {
    email?: string
    customer_id?: string
    billing_address?: Address | string
    shipping_address?: Address | string
    gift_cards?: GiftCard[]
    discounts?: Discount[]
} & CreateCartDto &
    WithCartId

export type AddLineItemDto = {
    metadata?: Record<string, unknown>
} & Pick<LineItem, 'variant_id' | 'quantity'> &
    WithCartId

export type UpdateLineItemDto = {
    line_id: string
} & Pick<LineItem, 'quantity'> &
    WithCartId

export type DeleteLineItemDto = {
    line_id: string
} & WithCartId

type CartResponse = {
    cart: Cart
}

export default class CartService {
    public static async createCart(dto: CreateCartDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>('/carts', dto)
        return data.cart
    }

    public static async getCart({ cart_id }: WithCartId): Promise<Cart> {
        const { data } = await axios.get<CartResponse>(`/carts/${cart_id}`)
        return data.cart
    }

    public static async updateCart({
        cart_id,
        ...dto
    }: UpdateCartDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${cart_id}`,
            dto,
        )
        return data.cart
    }

    // * Line-items
    public static async addLineItem({
        cart_id,
        ...dto
    }: AddLineItemDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${cart_id}/line-items`,
            dto,
        )
        return data.cart
    }

    public static async updateLineItem({
        cart_id,
        line_id,
        ...dto
    }: UpdateLineItemDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${cart_id}/line-items/${line_id}`,
            dto,
        )
        return data.cart
    }

    public static async deleteLineItem({
        cart_id,
        line_id,
    }: DeleteLineItemDto): Promise<Cart> {
        const { data } = await axios.delete<CartResponse>(
            `/carts/${cart_id}/line-items/${line_id}`,
        )
        return data.cart
    }

    public static async completeCart({ cart_id }: WithCartId) {
        const { data } = await axios.post(`/carts/${cart_id}/complete`)
        return data
    }
}
